/**
 * Данные формы генератора Автокрафта → тело запроса сборки. Общие для веба (форма точки)
 * и сервера (выгрузка макетов собирает недостающие PDF по сохранённым данным точки) —
 * одна функция, иначе макет в архиве и превью в панели собирались бы из разных данных.
 */
import type { AutocraftGeneratorSchema } from '../types/autocraft.js';
import {
  autocraftFieldDataBase,
  autocraftIndexedKey,
  createAutocraftSetCounter,
  isAutocraftArrayField,
  listAutocraftArrayVariantKeys,
  listAutocraftFieldInstances,
  withAutocraftLegacyArrayKeys,
} from './autocraftArrays.js';
import { getAutocraftField, type AutocraftFormValue } from './autocraftFields.js';

/**
 * Поля, которые наполняются значением из таблицы объектов (docs/wayfinding/plan.md §9.1.6):
 * всё, кроме групп и ссылок, — у них нет своего значения. Поля внутри группы-массива тоже
 * мимо: у них по значению на каждый набор, а строка таблицы даёт одно.
 */
export function autocraftFillableFieldKeys(schema: AutocraftGeneratorSchema): Set<string> {
  const keys = new Set<string>();
  for (const key of getAutocraftOrderedFieldKeys(schema)) {
    const field = getAutocraftField(schema, key);
    if (
      field &&
      field.type !== 'group' &&
      field.type !== 'link' &&
      !isAutocraftArrayField(schema, key)
    ) {
      keys.add(key);
    }
  }
  return keys;
}

export function getAutocraftOrderedFieldKeys(schema: AutocraftGeneratorSchema): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  const add = (key: string | undefined) => {
    if (!key || seen.has(key) || !getAutocraftField(schema, key)) {
      return;
    }
    seen.add(key);
    result.push(key);
  };
  for (const item of schema.dataForm?.items ?? []) {
    if (item.type === 'field') {
      add(item.fieldKey);
    }
  }
  for (const key of schema.dataModel._order ?? []) {
    add(key);
  }
  for (const key of Object.keys(schema.dataModel)) {
    add(key);
  }
  return result;
}

function isFormValue(value: unknown): value is AutocraftFormValue {
  return (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    value === null
  );
}

/**
 * Значения формы: сохранённые точкой, недостающее — умолчания генератора. У полей внутри
 * группы-массива — по значению на каждый набор (`stop1[0]`, `stop1[1]`); сколько наборов,
 * видно по сохранённым ключам, нет их — один набор.
 */
export function buildInitialAutocraftValues(
  schema: AutocraftGeneratorSchema,
  saved: Record<string, unknown> | null | undefined,
): Record<string, AutocraftFormValue> {
  // Ключ без внешних индексов у поля набора — его первые наборы (так читает и Автокрафт): так
  // лежат данные точек, собранных, пока схема не знала всей вложенности наборов.
  const source = withAutocraftLegacyArrayKeys(schema, saved ?? {});
  const countSets = createAutocraftSetCounter(schema, source);
  const result: Record<string, AutocraftFormValue> = {};
  for (const key of getAutocraftOrderedFieldKeys(schema)) {
    const field = getAutocraftField(schema, key);
    if (!field || field.type === 'link') {
      continue;
    }
    const base = autocraftFieldDataBase(key, field);
    for (const indices of listAutocraftFieldInstances(schema, countSets, key)) {
      const dataKey = autocraftIndexedKey(base, indices);
      const savedValue = source[dataKey];
      result[dataKey] = isFormValue(savedValue) ? savedValue : field.publicDefaultValue ?? '';
      if (field.isDeletable) {
        const visibilityKey = `@${dataKey}`;
        const savedVisibility = source[visibilityKey];
        result[visibilityKey] =
          typeof savedVisibility === 'boolean'
            ? savedVisibility
            : field.defaultDrawable !== false;
      }
      if (field.isArrayGroup) {
        for (const variantKey of listAutocraftArrayVariantKeys(countSets, key, indices)) {
          const savedVariant = source[variantKey];
          result[variantKey] = typeof savedVariant === 'string' ? savedVariant : '';
        }
      }
    }
  }
  return result;
}

/**
 * Тело `dataItems[0]` для Автокрафта. Без массивов и загрузок — ровно те же ключи, что и до их
 * появления: по телу считаются отпечатки обложек и ключи превью в бакете, и лишний ключ
 * пересобрал бы макеты всех точек разом.
 */
export function buildAutocraftPayload(
  schema: AutocraftGeneratorSchema,
  values: Record<string, AutocraftFormValue>,
): Record<string, AutocraftFormValue> {
  const countSets = createAutocraftSetCounter(schema, values);
  const result: Record<string, AutocraftFormValue> = {};
  for (const key of getAutocraftOrderedFieldKeys(schema)) {
    const field = getAutocraftField(schema, key);
    if (!field || field.type === 'link') {
      continue;
    }
    const base = autocraftFieldDataBase(key, field);
    for (const indices of listAutocraftFieldInstances(schema, countSets, key)) {
      const dataKey = autocraftIndexedKey(base, indices);
      if (field.isPublic !== false && field.type !== 'group') {
        result[dataKey] = values[dataKey] ?? field.publicDefaultValue ?? '';
      } else if (field.allowFileUpload && field.type !== 'group') {
        // Только загрузка, без выбора из списка: пустое значение не шлём — фрейм берёт
        // картинку шаблона, как и без ключа.
        const uploaded = values[dataKey];
        if (typeof uploaded === 'string' && uploaded !== '') {
          result[dataKey] = uploaded;
        }
      }
      if (field.isDeletable) {
        result[`@${dataKey}`] = values[`@${dataKey}`] !== false;
      }
      if (field.isArrayGroup) {
        for (const variantKey of listAutocraftArrayVariantKeys(countSets, key, indices)) {
          const variant = values[variantKey];
          // Пустой вариант — «обычный» набор; ключ не нужен.
          if (typeof variant === 'string' && variant !== '') {
            result[variantKey] = variant;
          }
        }
      }
    }
  }
  return result;
}
